import { useRef, type ReactNode } from "react";
import { LogoMark, MarkMotif } from "@mineoptics/brand/logo";
import { PillLink } from "@/components/PillLink";
import { gsap, useGSAP } from "@/lib/gsap";
import { useReducedMotion } from "@/lib/motion";
import { SIGNUP_URL } from "@/lib/site";

/*
 * Small interface vignettes drift in from depth, then collapse into a single
 * scenario card. All values shown are illustrative.
 */

const mono = "font-mono text-[0.625rem] leading-[1.5] tracking-[0.02em]";

function Collars() {
  const rows = [
    ["DH-001", "412 380", "6 541 220"],
    ["DH-002", "412 455", "6 541 198"],
    ["DH-003", "412 512", "6 541 260"],
    ["DH-004", "412 590", "6 541 305"],
  ];
  return (
    <table className={`${mono} w-full text-left`}>
      <thead className="text-mute">
        <tr>
          <th className="pb-1 font-normal">hole_id</th>
          <th className="pb-1 font-normal">easting</th>
          <th className="pb-1 font-normal">northing</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row[0]}>
            {row.map((cell) => (
              <td key={cell} className="tabular">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Pattern() {
  return (
    <svg viewBox="0 0 160 84" className="w-full" fill="none">
      {Array.from({ length: 4 }, (_, row) =>
        Array.from({ length: 7 }, (_, column) => (
          <circle key={`${row}-${column}`} cx={14 + column * 21 + (row % 2) * 10.5} cy={10 + row * 21} r="3" className="fill-lime" />
        )),
      )}
      <path d="M14 82h21M14 79v6M35 79v6" className="stroke-fg/60" />
      <path d="M152 10v21M149 10h6M149 31h6" className="stroke-fg/60" />
    </svg>
  );
}

function DrillOptions() {
  const options = [
    { label: "165 mm", width: "88%" },
    { label: "229 mm", width: "61%", best: true },
    { label: "270 mm", width: "72%" },
  ];
  return (
    <ul className="space-y-2.5">
      {options.map((option) => (
        <li key={option.label} className={mono}>
          <div className="mb-1 flex justify-between">
            <span>{option.label}</span>
            {option.best && <span className="text-lime">BEST $/t</span>}
          </div>
          <div className="h-1.5 rounded-full bg-fg/10">
            <div className={`h-full rounded-full ${option.best ? "bg-lime" : "bg-fg/45"}`} style={{ width: option.width }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function Cycle() {
  const circumference = 2 * Math.PI * 30;
  const parts = [
    { share: 0.14, className: "stroke-lime" },
    { share: 0.42, className: "stroke-fg/70" },
    { share: 0.1, className: "stroke-fg/35" },
    { share: 0.34, className: "stroke-fg/20" },
  ];
  let offset = 0;
  return (
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 80 80" className="h-16 w-16 -rotate-90 md:h-20 md:w-20" fill="none">
        {parts.map((part, index) => {
          const dash = part.share * circumference;
          const segment = (
            <circle
              key={index}
              cx="40"
              cy="40"
              r="30"
              strokeWidth="7"
              strokeDasharray={`${dash - 3} ${circumference - dash + 3}`}
              strokeDashoffset={-offset}
              className={part.className}
            />
          );
          offset += dash;
          return segment;
        })}
      </svg>
      <ul className={`${mono} hidden space-y-0.5 text-mute md:block`}>
        <li className="text-lime">Load</li>
        <li>Haul</li>
        <li>Dump</li>
        <li>Return</li>
      </ul>
    </div>
  );
}

function Contours() {
  return (
    <svg viewBox="0 0 160 84" className="w-full" fill="none">
      <path d="M-4 60C24 40 44 70 78 52s54-30 90-12" className="stroke-fg/25" />
      <path d="M-4 44C26 26 50 52 80 36s52-28 88-10" className="stroke-fg/25" />
      <path d="M-4 76c30-16 52 10 84-6s54-26 88-10" className="stroke-fg/25" />
      <ellipse cx="96" cy="44" rx="44" ry="26" className="stroke-fg/45" />
      <ellipse cx="98" cy="45" rx="31" ry="18" className="stroke-fg/60" />
      <ellipse cx="100" cy="46" rx="18" ry="10" className="stroke-lime" />
    </svg>
  );
}

function KeyValues({ rows }: { rows: [string, string][] }) {
  return (
    <dl className={`${mono} space-y-1`}>
      {rows.map(([key, value]) => (
        <div key={key} className="flex justify-between gap-4">
          <dt className="text-mute">{key}</dt>
          <dd className="tabular">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Flow() {
  return (
    <ol className={`${mono} space-y-1.5`}>
      {["Primary", "Secondary", "Tertiary"].map((stage, index) => (
        <li key={stage} className="flex items-center gap-2">
          <span className={`h-1.5 w-1.5 rounded-full ${index === 0 ? "bg-lime" : "bg-fg/35"}`} />
          <span className={`flex-1 rounded-full border px-2.5 py-1 ${index === 0 ? "border-lime text-lime" : "border-fg/25"}`}>
            {stage}
          </span>
        </li>
      ))}
    </ol>
  );
}

interface Tile {
  label: string;
  content: ReactNode;
  /** Resting place as a share of the viewport (vw, vh) from the centre, and its tilt. */
  x: number;
  y: number;
  tilt: number;
  /** Position on small screens; tiles without one are desktop only. */
  small?: [number, number];
}

const TILES: Tile[] = [
  { label: "Blast pattern", content: <Pattern />, x: -30, y: -29, tilt: 20, small: [-25, -32] },
  { label: "Collars.csv", content: <Collars />, x: -40, y: 2, tilt: 26 },
  { label: "Drill options", content: <DrillOptions />, x: -31, y: 30, tilt: 20, small: [-25, 31] },
  {
    label: "Rock data",
    content: <KeyValues rows={[["Density", "2.70 t/m³"], ["UCS", "120 MPa"], ["Hardness", "Medium"]]} />,
    x: -11,
    y: 37,
    tilt: 8,
  },
  { label: "Topography", content: <Contours />, x: 30, y: -28, tilt: -20, small: [25, -30] },
  {
    label: "Vendor quote · Active",
    content: <KeyValues rows={[["Class", "Haul truck"], ["Capital / unit", "per quote"], ["Operating / h", "per quote"]]} />,
    x: 40,
    y: 0,
    tilt: -26,
  },
  { label: "Haul cycle", content: <Cycle />, x: 31, y: 29, tilt: -20, small: [25, 32] },
  { label: "Crushing", content: <Flow />, x: 11, y: 38, tilt: -8 },
];

const COST_ROWS = [
  { label: "Drilling & blasting", width: "34%" },
  { label: "Loading & hauling", width: "86%" },
  { label: "Processing", width: "58%" },
  { label: "Support services", width: "27%" },
];

export function Converge() {
  const root = useRef<HTMLElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();

  useGSAP(
    () => {
      if (reduced) return;

      const small = () => window.innerWidth < 768;
      const place = (tile: Tile) => (small() && tile.small ? tile.small : [tile.x, tile.y]);
      const restX = (tile: Tile) => (place(tile)[0] / 100) * window.innerWidth;
      const restY = (tile: Tile) => (place(tile)[1] / 100) * window.innerHeight;

      const timeline = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: {
          trigger: stage.current,
          start: "top top",
          end: () => `+=${window.innerHeight * 2.8}`,
          pin: true,
          scrub: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      gsap.utils.toArray<HTMLElement>("[data-tile]").forEach((element, index) => {
        const tile = TILES[index];
        gsap.set(element, { xPercent: -50, yPercent: -50 });
        timeline.fromTo(
          element,
          { x: () => restX(tile) * 2.1, y: () => restY(tile) * 2.1, z: -1200, rotateY: tile.tilt * 1.6, autoAlpha: 0 },
          { x: () => restX(tile), y: () => restY(tile), z: 0, rotateY: tile.tilt, autoAlpha: 1, duration: 0.3, ease: "power2.out" },
          0.015 * index,
        );
        timeline.to(
          element,
          { x: 0, y: 0, z: -260, rotateY: 0, scale: 0.28, autoAlpha: 0, duration: 0.26, ease: "power2.in" },
          0.46 + 0.02 * index,
        );
      });

      timeline
        .to("[data-converge-lead]", { autoAlpha: 0, scale: 0.82, duration: 0.14, ease: "power1.in" }, 0.4)
        .fromTo(
          "[data-converge-card]",
          { autoAlpha: 0, scale: 0.7, y: 60 },
          { autoAlpha: 1, scale: 1, y: 0, duration: 0.24, ease: "power2.out" },
          0.58,
        )
        .fromTo("[data-converge-bar]", { scaleX: 0 }, { scaleX: 1, duration: 0.14, stagger: 0.035, ease: "power2.out" }, 0.7)
        .fromTo("[data-converge-after]", { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: 0.1, stagger: 0.03 }, 0.86)
        .to({}, { duration: 0.08 });
    },
    { scope: root, dependencies: [reduced] },
  );

  const card = (
    <div data-converge-card className="w-[min(88vw,29rem)] rounded-3xl border border-fg/15 bg-surface p-6 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.9)] md:p-7">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <LogoMark className="h-9 w-9 shrink-0" />
          <div>
            <p className="eyebrow text-mute">Scenario</p>
            <p className="mt-1.5 text-2xl font-medium tracking-[-0.03em]">Base case</p>
          </div>
        </div>
        <span className="eyebrow flex items-center gap-2 rounded-full border border-lime/40 px-3 py-1.5 text-lime">
          <span className="h-1.5 w-1.5 rounded-full bg-lime" />
          Calculated
        </span>
      </div>
      <ul className="mt-7 space-y-4">
        {COST_ROWS.map((row) => (
          <li key={row.label}>
            <div className="mb-1.5 flex justify-between text-sm">
              <span>{row.label}</span>
              <span className="font-mono text-xs text-mute">$ / t</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-fg/10">
              <div data-converge-bar className="h-full origin-left rounded-full bg-lime" style={{ width: row.width }} />
            </div>
          </li>
        ))}
      </ul>
      <p className="eyebrow mt-7 flex justify-between border-t border-fg/10 pt-4 text-mute">
        <span>North pit · 5.0 Mt / y</span>
        <span>Illustrative</span>
      </p>
    </div>
  );

  if (reduced) {
    return (
      <section data-theme="dark" className="bg-bg">
        <div className="shell flex flex-col items-center gap-10 py-20 text-center">
          <h2 className="title">
            Every input.
            <br />
            One model.
          </h2>
          <div className="text-left">{card}</div>
          <PillLink to={SIGNUP_URL} label="Build your first scenario" variant="accent" arrow />
        </div>
      </section>
    );
  }

  return (
    <section ref={root} data-theme="dark" className="relative bg-bg">
      <div ref={stage} className="relative h-[100svh] overflow-hidden [perspective:1300px]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_50%,rgba(203,255,84,0.07),transparent_70%)]" />
        <MarkMotif
          rings={7}
          dot={false}
          strokeWidth={0.22}
          className="pointer-events-none absolute left-1/2 top-1/2 h-[max(125vh,72rem)] w-[max(125vh,72rem)] -translate-x-1/2 -translate-y-1/2 text-fg/[0.13]"
        />

        <div data-converge-lead className="absolute inset-0 flex items-center justify-center px-5">
          <h2 data-split className="display text-center">
            Every input.
          </h2>
        </div>

        {TILES.map((tile) => (
          <div
            key={tile.label}
            data-tile
            className={`absolute left-1/2 top-1/2 w-[9.25rem] rounded-2xl border border-fg/15 bg-surface p-3.5 will-change-transform md:w-[13.5rem] md:p-4 ${
              tile.small ? "" : "hidden md:block"
            }`}
          >
            <p className="eyebrow mb-3 text-mute">{tile.label}</p>
            {tile.content}
          </div>
        ))}

        <div className="absolute inset-0 flex flex-col items-center justify-center gap-7 px-5 md:gap-9">
          <p data-converge-after className="title text-center">
            One model.
          </p>
          {card}
          <div data-converge-after>
            <PillLink to={SIGNUP_URL} label="Build your first scenario" variant="accent" arrow />
          </div>
        </div>
      </div>
    </section>
  );
}
